<template>
	<div v-if="categoryTree.length > 0" class="space-y-xs">
		<v-category-filter-category-tree-node
			v-for="node in categoryTree"
			:key="node.id"
			:node="node"
			:selected-facets="selectedFacets"
			:parent-checked="false"
			:parent-id="undefined"
			@toggle="toggleNode"
		/>
	</div>
</template>

<script lang="ts" setup>
import type {TextFacet, TextFacetValue} from "@apptus/esales-api";
import {useVModel} from "@vueuse/core";
import {uniq} from "es-toolkit";
import type {Category} from "@pkg/shared/graphql";
import type {CategoryTreeNode} from "@pkg/shared/utils/build-category-tree";

import {useCategories} from "@pkg/shared/composables/use-categories";
import {buildCategoryTree} from "@pkg/shared/utils/build-category-tree";

export type CategoryTreeNodeWithFacet = CategoryTreeNode & {
	facet: TextFacetValue;
	children: CategoryTreeNodeWithFacet[];
};

const properties = defineProps<{
	facet: TextFacet;
	modelValue?: (string | number | boolean)[];
}>();

const emit = defineEmits<{
	(event: "update:modelValue", value?: (string | number)[]): void;
}>();

const selectedFacets = useVModel(properties, "modelValue", emit);

const {data: categories} = await useCategories();

// Map category (title) to given facet (label)
// Unmapped facets go the end
const categoryTree = computed<CategoryTreeNodeWithFacet[]>(() => {
	const matchedFacetLabels: Map<string, true> = new Map();

	const tree = buildCategoryTree(
		categories.value as Category[],
		1,
		(category) => {
			// Find the facet matching to this category, identified just by its title since elevate doesn't return us any other info about the facet values here. Generally categories should have unique titles, even between sub-categories of different parents.
			// If there's no or multiple matches, we don't try to put this category into the tree at all, instead the corresponding facet will be rendered on its own at the end of the categories list/tree.
			const matchingFacets = properties.facet.values.filter(
				(f) => f.label === category.title,
			);
			const matchingFacet = matchingFacets[0];

			// Leave out this category from the facets tree as said above
			if (matchingFacets.length !== 1 || !matchingFacet) {
				return undefined;
			}

			// Take note that this facet has been found a matching category
			matchedFacetLabels.set(matchingFacet.label, true);

			return {
				...category,
				facet: matchingFacet,
			};
		},
	) as CategoryTreeNodeWithFacet[]; // trust me bro

	// These facets didn't have a matching category on our end
	const unmatchedFacets = properties.facet.values.filter(
		(facet) => !matchedFacetLabels.has(facet.label),
	);

	return [
		...tree,
		...unmatchedFacets.map((facet, index): CategoryTreeNodeWithFacet => ({
			id: `facet-${index}`,
			children: [],
			facet,
			title: facet.label,
		})),
	];
});

const toggleNode = (
	nodeId: string,
	value: boolean,
	childIds: string[],
	parentId?: string,
) => {
	if (value) {
		// Special case: if parentId is provided, remove parent and add only this child
		if (parentId) {
			const withoutParent =
				selectedFacets.value?.filter((item) => item !== parentId) || [];
			selectedFacets.value = uniq([...withoutParent, nodeId]);
		} else {
			// Normal case: When checking a node, remove all its descendants and add only the node
			const withoutDescendants =
				selectedFacets.value?.filter(
					(item) => !childIds.includes(item as string),
				) || [];
			selectedFacets.value = uniq([...withoutDescendants, nodeId]);
		}
	} else {
		// When unchecking a node, just remove it
		selectedFacets.value = uniq(
			selectedFacets.value?.filter((item) => item !== nodeId) || [],
		);
	}
};
</script>
